import React, { useState, useEffect } from 'react';
import { lineStationAPI, lineAPI, stationAPI, cityAPI } from '../services/api';
import { LineStation, Line, Station, City } from '../types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Divider
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';

// Define proper error type
interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}

const LineStations: React.FC = () => {
  const [lineStations, setLineStations] = useState<LineStation[]>([]);
  const [lines, setLines] = useState<Line[]>([]);
  const [stations, setStations] = useState<Station[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [selectedCityId, setSelectedCityId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [loadingCities, setLoadingCities] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    lineId: '',
    fromStationId: '',
    toStationId: '',
    platformNumber: 1,
    platformSide: 'LEFT'
  });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    fetchCities();
  }, []);

  useEffect(() => {
    if (selectedCityId) {
      fetchDataByCity();
    } else {
      // Clear data when no city is selected
      setLines([]);
      setStations([]);
      setLineStations([]);
    }
  }, [selectedCityId]);

  const fetchCities = async () => {
    setLoadingCities(true);
    try {
      const res = await cityAPI.getAll();
      setCities(Array.isArray(res.data) ? res.data : []);
    } catch (e: unknown) {
      const error = e as ApiError;
      console.error('Failed to load cities:', error);
    } finally {
      setLoadingCities(false);
    }
  };

  const fetchDataByCity = async () => {
    if (!selectedCityId) return;
    
    setLoading(true);
    setError(null);
    try {
      const [linesRes, stationsRes] = await Promise.all([
        lineAPI.getByCity(selectedCityId),
        stationAPI.getByCity(selectedCityId)
      ]);
      
      const fetchedLines = Array.isArray(linesRes.data) ? linesRes.data : [];
      const fetchedStations = Array.isArray(stationsRes.data) ? stationsRes.data : [];
      
      setLines(fetchedLines);
      setStations(fetchedStations);
      
      // Fetch line stations for the lines in this city
      if (fetchedLines.length > 0) {
        const lineIds = fetchedLines.map(line => line.id).filter(Boolean) as string[];
        const lineStationPromises = lineIds.map(lineId => 
          lineStationAPI.getByLine(lineId).catch(() => ({ data: [] }))
        );
        const lineStationResults = await Promise.all(lineStationPromises);
        const allLineStations = lineStationResults
          .flatMap(res => Array.isArray(res.data) ? res.data : [])
          .filter((ls, index, self) => 
            index === self.findIndex(t => t.id === ls.id)
          );
        setLineStations(allLineStations);
      } else {
        setLineStations([]);
      }
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCityChange = (cityId: string) => {
    setSelectedCityId(cityId);
    // Reset form when city changes
    setForm({
      lineId: '',
      fromStationId: '',
      toStationId: '',
      platformNumber: 1,
      platformSide: 'LEFT'
    });
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setForm({ 
      ...form, 
      [name]: type === 'number' ? parseInt(value) || 0 : value 
    });
    setFormError('');
  };

  const handleSelectInput = (e: SelectChangeEvent<string>) => {
    const name = e.target.name as string;
    const value = e.target.value;
    setForm({ ...form, [name]: value });
    setFormError('');
  };

  const openAddModal = () => {
    setIsEditMode(false);
    setEditingId(null);
    setForm({
      lineId: '',
      fromStationId: '',
      toStationId: '',
      platformNumber: 1,
      platformSide: 'LEFT'
    });
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (lineStation: LineStation) => {
    setIsEditMode(true);
    setEditingId(lineStation.id!);
    setForm({
      lineId: lineStation.lineId,
      fromStationId: lineStation.fromStationId || '',
      toStationId: lineStation.toStationId || '',
      platformNumber: lineStation.platformNumber || 1,
      platformSide: lineStation.platformSide || 'LEFT'
    });
    setFormError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormError('');
  };

  const handleSave = async () => {
    if (!form.lineId) {
      setFormError('Line is required');
      return;
    }
    if (!form.fromStationId || !form.toStationId) {
      setFormError('From Station and To Station are required');
      return;
    }
    if (!form.platformNumber || form.platformNumber < 1) {
      setFormError('Platform number is required and must be at least 1');
      return;
    }
    if (!form.platformSide) {
      setFormError('Platform side is required');
      return;
    }

    setSaving(true);
    try {
      if (isEditMode && editingId) {
        // For update, only send platform fields
        await lineStationAPI.update(editingId, {
          platformNumber: form.platformNumber,
          platformSide: form.platformSide
        });
        setSuccessMsg('Platform information updated successfully');
      } else {
        // For create, send all fields
        await lineStationAPI.create(form);
        setSuccessMsg('Line Station platform information created successfully');
      }
      await fetchDataByCity();
      closeModal();
    } catch (e: unknown) {
      const error = e as ApiError;
      setFormError(error.response?.data?.message || error.message || 'Failed to save line station');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this line station?')) return;
    
    setDeletingId(id);
    try {
      await lineStationAPI.delete(id);
      setSuccessMsg('Line Station deleted successfully');
      await fetchDataByCity();
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to delete line station');
    } finally {
      setDeletingId(null);
    }
  };

  const getLineName = (lineId: string) => {
    const line = lines.find(l => l.id === lineId);
    return line?.name || 'Unknown Line';
  };

  const getStationName = (stationId: string) => {
    const station = stations.find(s => s.id === stationId);
    return station?.name || 'Unknown Station';
  };

  if (loadingCities) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, px: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Line Stations
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<Add />} 
          onClick={openAddModal}
          disabled={!selectedCityId}
        >
          Add Line Station
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mx: 3, mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* City Selector */}
      <Paper sx={{ mx: 3, mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Select City</InputLabel>
              <Select
                value={selectedCityId}
                onChange={(e) => handleCityChange(e.target.value)}
                label="Select City"
              >
                <MenuItem value="">
                  <em>Select a city to view line stations</em>
                </MenuItem>
                {Array.isArray(cities) && cities.map((city) => (
                  <MenuItem key={city.id} value={city.id}>
                    {city.name} ({city.country})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {selectedCityId && (
            <Grid item xs={12} md={8}>
              <Box display="flex" alignItems="center" gap={1}>
                <Chip 
                  label={`Showing data for: ${cities.find(c => c.id === selectedCityId)?.name || 'Unknown'}`} 
                  color="primary" 
                  variant="outlined"
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleCityChange('')}
                >
                  Clear
                </Button>
                {loading && <CircularProgress size={20} />}
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>

      {!selectedCityId && (
        <Alert severity="info" sx={{ mx: 3, mb: 3 }}>
          Please select a city to view and manage line stations.
        </Alert>
      )}

      {selectedCityId && (
        <Paper sx={{ mx: 3, mb: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Line</TableCell>
                <TableCell>Route</TableCell>
                <TableCell>Platform</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.isArray(lineStations) && lineStations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No platform information configured. Click "Add Line Station" to create one.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                Array.isArray(lineStations) && lineStations.map((lineStation) => (
                  <TableRow key={lineStation.id}>
                    <TableCell>{getLineName(lineStation.lineId)}</TableCell>
                    <TableCell>
                      {lineStation.fromStationId && lineStation.toStationId ? (
                        <Typography variant="body2">
                          <strong>{getStationName(lineStation.fromStationId)}</strong> → <strong>{getStationName(lineStation.toStationId)}</strong>
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">Not Set</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {lineStation.platformNumber ? (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', display: 'inline' }}>
                            Platform {lineStation.platformNumber}
                          </Typography>
                          {lineStation.platformSide && lineStation.platformSide !== 'BOTH' && (
                            <Typography variant="caption" sx={{ ml: 0.5, color: 'text.secondary' }}>
                              ({lineStation.platformSide})
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">Not Set</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Edit Platform Info">
                        <IconButton onClick={() => openEditModal(lineStation)} size="small">
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton 
                          onClick={() => handleDelete(lineStation.id!)} 
                          size="small"
                          disabled={deletingId === lineStation.id}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onClose={closeModal} maxWidth="lg" fullWidth>
        <DialogTitle>
          {isEditMode ? 'Edit Line Station' : 'Add Line Station'}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Configure platform information for a specific route (from station → to station) on a line.
              If an entry already exists for the same route, it will be updated instead of creating a duplicate.
            </Typography>
          </Alert>
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Line *</InputLabel>
                <Select
                  name="lineId"
                  value={form.lineId}
                  onChange={handleSelectInput}
                  label="Line *"
                  disabled={isEditMode}
                >
                  <MenuItem value="">
                    <em>Select a line</em>
                  </MenuItem>
                  {Array.isArray(lines) && lines.map((line) => (
                    <MenuItem key={line.id} value={line.id}>
                      {line.name} ({line.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>From Station (Boarding) *</InputLabel>
                <Select
                  name="fromStationId"
                  value={form.fromStationId}
                  onChange={handleSelectInput}
                  label="From Station (Boarding) *"
                  disabled={isEditMode}
                >
                  <MenuItem value="">
                    <em>Select boarding station</em>
                  </MenuItem>
                  {Array.isArray(stations) && stations.map((station) => (
                    <MenuItem key={station.id} value={station.id}>
                      {station.name} ({station.stationCode})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>To Station (Destination) *</InputLabel>
                <Select
                  name="toStationId"
                  value={form.toStationId}
                  onChange={handleSelectInput}
                  label="To Station (Destination) *"
                  disabled={isEditMode}
                >
                  <MenuItem value="">
                    <em>Select destination station</em>
                  </MenuItem>
                  {Array.isArray(stations) && stations.map((station) => (
                    <MenuItem key={station.id} value={station.id}>
                      {station.name} ({station.stationCode})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Platform Number *"
                name="platformNumber"
                type="number"
                value={form.platformNumber}
                onChange={handleInput}
                inputProps={{ min: 1 }}
                helperText="Platform number where passengers board for this route"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Platform Side *</InputLabel>
                <Select
                  name="platformSide"
                  value={form.platformSide}
                  onChange={handleSelectInput}
                  label="Platform Side *"
                >
                  <MenuItem value="LEFT">Left</MenuItem>
                  <MenuItem value="RIGHT">Right</MenuItem>
                  <MenuItem value="BOTH">Both</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Route Information Display */}
          {form.lineId && form.fromStationId && form.toStationId && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Route Information:
                </Typography>
                <Typography variant="body2">
                  <strong>Line:</strong> {getLineName(form.lineId)}
                </Typography>
                <Typography variant="body2">
                  <strong>Route:</strong> {getStationName(form.fromStationId)} → {getStationName(form.toStationId)}
                </Typography>
                <Typography variant="body2">
                  <strong>Platform:</strong> {form.platformNumber} ({form.platformSide})
                </Typography>
              </Paper>
            </Box>
          )}

          {formError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {formError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Message */}
      <Snackbar
        open={!!successMsg}
        autoHideDuration={6000}
        onClose={() => setSuccessMsg('')}
      >
        <Alert onClose={() => setSuccessMsg('')} severity="success">
          {successMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};


export default LineStations; 